/**
 * Lit Legion reward panels.
 *
 * Builds ONE panel definition per task and lets the call site choose how it is
 * presented — inline on the task page (what you'll get), or modally after a
 * successful claim (what you got). That is the whole point of the panel
 * primitive: presentation is a decision at the call site, not a property of the
 * content. See `docs/panels.md`.
 *
 * The reward list itself is runtime data supplied through the render context,
 * not authored into the definition, so the same definition renders "0 items"
 * before a claim and the real list after one.
 */
import type { ReactNode } from 'react';
import type { PanelContent, PanelItem } from '@fias/panel-kit';
import type { LitLegionCoinReward, LitLegionBadgeReward } from '@/lib/api';

export interface TaskRewardInput {
  xpReward: number;
  coinRewards: LitLegionCoinReward[];
  badgeRewards: LitLegionBadgeReward[];
}

/**
 * Icon nodes per reward kind, supplied by the calling page. Keyed rather than
 * inferred so the mapping is explicit — `PanelItem.icon` is runtime-only and
 * never authored, which is what lets an internal arche keep its icon components
 * instead of having to publish icon URLs.
 */
export interface RewardIcons {
  xp?: ReactNode;
  coin?: ReactNode;
  badge?: ReactNode;
}

/** The reward list as panel items, in display order: XP, coins, badges. */
export function buildRewardItems(
  { xpReward, coinRewards, badgeRewards }: TaskRewardInput,
  icons: RewardIcons = {},
): PanelItem[] {
  const items: PanelItem[] = [];
  if (xpReward > 0) items.push({ label: 'XP', amount: xpReward, icon: icons.xp });
  for (const coin of coinRewards) {
    items.push({ label: coin.coinName, amount: coin.quantity, icon: icons.coin });
  }
  for (const badge of badgeRewards) {
    items.push({ label: badge.badgeId, icon: icons.badge });
  }
  return items;
}

/**
 * The task's reward panel, shown INLINE on the task page before the claim.
 * `emptyText` covers the "no rewards specified" case the page used to render as
 * a bespoke line.
 */
export function buildTaskRewardsPanel(): PanelContent {
  return {
    id: 'lit-legion-task-rewards',
    title: 'Rewards',
    elements: [{ kind: 'items', emptyText: 'No rewards specified' }],
  };
}

/**
 * The same content, presented MODALLY after a successful claim. Only the
 * wording and the acknowledge button differ — both render through the same
 * `<Panel>` element list.
 *
 * The button is `emit:ok` rather than `close`: the page decides what
 * acknowledging means, and the platform never learns a Lit Legion verb.
 *
 * The `{taskName}` placeholder is filled at display time from the render
 * context — the caller passes `{ values: { taskName } }`.
 */
export function buildClaimSuccessPanel(): PanelContent {
  return {
    id: 'lit-legion-claim-success',
    title: 'Quest complete',
    elements: [
      { kind: 'text', text: '{taskName}', style: 'title' },
      { kind: 'items', emptyText: 'Your progress has been recorded.' },
      { kind: 'button', label: 'OK', action: 'emit:ok' },
    ],
  };
}
